import { useEffect, useMemo, useState } from 'react';
import {
  evaluateAlerts, worstSeverity,
  type AlertSeverity, type RigAlert, type ShowMessage,
} from '@micplot/shared';
import {
  useStore, useAssignments, useTelemetry, useClockSkew, useRfHistory, useRfLive,
  useRfEverLive,
} from '../state/store';
import { useNowMinutes } from '../lib/now';
import { api } from '../lib/api';

/**
 * The things you must not miss, on every screen.
 *
 * Two different problems with the same answer. A pack about to die and a
 * message from the other end of the headset are both useless if they are
 * sitting on a tab you are not looking at -- and during a show you are looking
 * at the board, not at a notifications panel. So both land here, below the
 * header, on whichever view you happen to be on.
 *
 * Critical is deliberately loud: full-width, red, and it pulses. It is the one
 * piece of the interface allowed to be annoying, because the alternative is a
 * dead mic in front of an audience. Warnings sit quietly underneath and can be
 * collapsed.
 */
/** How many rows the bar may take before the rest collapse behind a summary. */
const MAX_ROWS = 2;

/** Matches the server's STALE_AFTER_MS, so both agree the feed has stopped. */
const STALE_FEED_SEC = 15;

export default function AlertBar() {
  const identity = useStore(s => s.identity);
  const state = useStore(s => s.state);
  const telemetry = useTelemetry();
  const assignments = useAssignments();
  const notify = useStore(s => s.notify);
  const now = useNowMinutes();
  const skew = useClockSkew();
  const history = useRfHistory();
  const bridgeLive = useRfLive();
  const bridgeEverLive = useRfEverLive();
  /*
   * A ticker of our own, because silence is the signal.
   *
   * rfLive only ever changes when a telemetry message arrives, and the whole
   * point of a dead bridge is that no message is coming. Nothing would ever
   * re-render to notice, so the feed's age has to be re-checked on a clock
   * we own rather than on the arrival of data that has stopped.
   */
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(n => n + 1), 5_000);
    return () => clearInterval(t);
  }, []);

  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const alerts = useMemo(() => {
    if (!state) return [];
    return evaluateAlerts({
      telemetry,
      channels: state.channels,
      assignments,
      presenters: state.presenters,
      now,
      // Server time, not ours: a reading is old relative to the clock that
      // stamped it.
      nowMs: Date.now() + skew,
      history,
      bridgeLive,
    });
  }, [state, telemetry, assignments, now, skew, history, bridgeLive]);

  /*
   * Losing the feed is itself the most serious thing the rig can report.
   *
   * When the bridge stops, every reading is filtered out and every alert
   * silently evaluates to nothing -- a screen indistinguishable from a show
   * that never had a bridge at all. Anything that goes wrong from then on goes
   * unreported, so the one thing left to say is that we can no longer see.
   */
  /*
   * Judged from the readings themselves, not from a flag somebody has to send
   * us. STALE_FEED_SEC matches the server's own cut-off for handing a reading
   * over, so the two agree about when the rig has gone quiet.
   */
  const newest = telemetry.reduce((m, t) => Math.max(m, t.updatedAt), 0);
  const feedAgeSec = newest === 0 ? Infinity : (Date.now() + skew - newest) / 1000;
  const feedGone = !bridgeLive || feedAgeSec > STALE_FEED_SEC;

  const blind: RigAlert | null = feedGone && bridgeEverLive
    ? {
      kind: 'telemetry-stale',
      severity: 'critical',
      message: 'Lost contact with the rig — no readings are coming in',
      advice: 'Nothing about batteries, RF or audio is being monitored until '
        + 'this comes back. Check the bridge machine and its network.',
      key: 'bridge:lost',
    }
    : null;

  /*
   * A dismissal sticks to the condition, not to the moment. The key is derived
   * from the receiver, channel and kind, so silencing "TX 4 battery low" stays
   * silenced while it is low and comes back if it goes critical.
   */
  const live = [...(blind ? [blind] : []), ...alerts].filter(a => !dismissed.has(a.key));

  /*
   * Forget a dismissal once the condition it silenced has cleared.
   *
   * The key is derived from the receiver, channel and kind, so without this a
   * dismissal is permanent: silencing an RF warning at 19:10 after checking
   * the aerial also silences a genuinely new one at 21:40, and the operator is
   * never told. Silencing should mean "not now", not "not tonight".
   */
  const currentKeys = useMemo(
    () => new Set([...(blind ? [blind.key] : []), ...alerts.map(a => a.key)]),
    [alerts, blind],
  );
  useEffect(() => {
    setDismissed(prev => {
      const next = new Set([...prev].filter(k => currentKeys.has(k)));
      return next.size === prev.size ? prev : next;
    });
  }, [currentKeys]);

  // An urgent message stays on screen until somebody acknowledges it. That is
  // the whole point of marking it urgent.
  const urgent: ShowMessage[] = (state?.messages ?? [])
    .filter(m => m.urgent && !m.ackAt);

  const critical = live.filter(a => a.severity === 'critical');
  const warnings = live.filter(a => a.severity === 'warning');
  const notes = live.filter(a => a.severity === 'info');
  const lesser = [...warnings, ...notes];
  // What is folded away, not what exists: pairing "2 more" with a count of
  // rows already on screen reads as though those two are the hidden ones.
  const hiddenCritical = Math.max(0, critical.length - MAX_ROWS);
  const hiddenUrgent = Math.max(0, urgent.length - MAX_ROWS);
  const hidden = hiddenCritical + hiddenUrgent + lesser.length;
  const worst: AlertSeverity | null = urgent.length > 0 ? 'critical' : worstSeverity(live);

  if (!state || (live.length === 0 && urgent.length === 0)) return null;

  const ack = (m: ShowMessage) => {
    if (!identity) return;
    api.ackMessage(identity.code, m.id)
      .catch(e => notify((e as Error).message, 'error'));
  };

  return (
    <div className={`alertbar ${worst ?? 'info'}`} role="alert" aria-live="assertive">
      {/* Same row budget as the alerts: an unacknowledged pile-up during a
          changeover must not push the board off the screen. */}
      {urgent.slice(0, MAX_ROWS).map(m => (
        <div className="alertbar-row urgent" key={m.id}>
          <span className="alertbar-tag">MESSAGE</span>
          <span className="alertbar-text">
            <strong>{m.authorName}:</strong> {m.text}
          </span>
          {/* Acknowledging tells the sender it landed, which is why they are
              allowed to interrupt in the first place. */}
          <button className="btn sm primary" onClick={() => ack(m)}>Got it</button>
        </div>
      ))}

      {/*
        * Only the worst few get a row of their own. Every bar this app stacks
        * -- position, message, alerts -- comes out of the same screen the
        * operator is actually working on, and with six channels reporting it
        * was taking near half the height of a laptop at the desk. The rest
        * collapse into one line that can be opened.
        */}
      {critical.slice(0, MAX_ROWS).map(a => (
        <div className="alertbar-row critical" key={a.key}>
          <span className="alertbar-tag">{tagFor(a)}</span>
          <span className="alertbar-text">
            {a.message}
            {/* What to do, when there is a specific answer. During a show the
                gap between knowing and doing is where the time goes. */}
            {a.advice && <em className="alertbar-advice">{a.advice}</em>}
          </span>
          <button className="alertbar-x" aria-label={`Silence: ${a.message}`}
                  onClick={() => setDismissed(s => new Set(s).add(a.key))}>✕</button>
        </div>
      ))}

      {hidden > 0 && (
        <div className="alertbar-row summary">
          <button className="alertbar-more"
                  aria-expanded={expanded}
                  onClick={() => setExpanded(v => !v)}>
            {expanded ? '▾ Hide' : `▸ ${hidden} more`}
            {' · '}
            {/* Named counts, so the collapsed line still says how bad it is. */}
            {[
              hiddenUrgent > 0 && `${hiddenUrgent} more message${hiddenUrgent > 1 ? 's' : ''}`,
              hiddenCritical > 0 && `${hiddenCritical} serious`,
              warnings.length > 0 && `${warnings.length} to check`,
              notes.length > 0 && `${notes.length} note${notes.length > 1 ? 's' : ''}`,
            ].filter(Boolean).join(' · ')}
          </button>
        </div>
      )}

      {expanded && urgent.slice(MAX_ROWS).map(m => (
        <div className="alertbar-row urgent" key={m.id}>
          <span className="alertbar-tag">MESSAGE</span>
          <span className="alertbar-text">
            <strong>{m.authorName}:</strong> {m.text}
          </span>
          <button className="btn sm primary" onClick={() => ack(m)}>Got it</button>
        </div>
      ))}

      {expanded && [...critical.slice(MAX_ROWS), ...lesser].map(a => (
        <div className={`alertbar-row ${a.severity}`} key={a.key}>
          <span className="alertbar-tag">{tagFor(a)}</span>
          <span className="alertbar-text">
            {a.message}
            {a.advice && <em className="alertbar-advice">{a.advice}</em>}
          </span>
          <button className="alertbar-x" aria-label={`Silence: ${a.message}`}
                  onClick={() => setDismissed(s => new Set(s).add(a.key))}>✕</button>
        </div>
      ))}
    </div>
  );
}

/** A short word for the column, so the eye can sort them without reading. */
function tagFor(a: RigAlert): string {
  switch (a.kind) {
    case 'battery-critical':
    case 'battery-low':
    case 'battery-short-of-call': return 'BATT';
    case 'rf-low': return 'RF';
    case 'rf-dropouts': return 'RF';
    case 'antenna-imbalance': return 'ANT';
    case 'frequency-unplotted': return 'PLOT';
    case 'channel-not-received': return 'RX';
    case 'audio-silent':
    case 'audio-clipping': return 'AUDIO';
    case 'muted-on-air': return 'MUTE';
    case 'telemetry-stale': return 'LINK';
    case 'frequency-mismatch': return 'PLOT';
  }
}
